import * as Yup from 'yup';

import { getUploadedFile } from 'utils/helpers';
import { formatDate, formatCalendarDate } from 'utils/date';
import {
  IdObject,
  AccountingEvent,
  AccountingExpense,
  AccountingExpenseRentalDetail,
} from 'types';

import { FormValues } from './types';

export const eventInitialValues = {
  amount: undefined,
  eventId: undefined,
  percent: undefined,
};

// Owner ruling (H7, 2026-08-19), re-verified read-only on Production
// 2026-10-13: ExpenseCategories id 16 = "Car Rental", company 3. Categories
// are freeform per-company names with no type flag, so the id is the gate --
// a name match would be a guess about intent.
export const CAR_RENTAL_CATEGORY_ID = 16;

export const isCarRentalSelected = (categories: (number | string)[] = []) =>
  categories.some((id) => Number(id) === CAR_RENTAL_CATEGORY_ID);

// Mirrors enum_ExpenseRentalDetails_rentalCompany. The server upper-cases but
// refuses anything outside these four, so this is a select, not free text.
export const RENTAL_COMPANY_OPTIONS = [
  { value: 'ENTERPRISE', label: 'Enterprise' },
  { value: 'AVIS', label: 'Avis' },
  { value: 'BUDGET', label: 'Budget' },
  { value: 'OTHER', label: 'Other' },
];

// Column widths of ExpenseRentalDetails (renterName varchar(160),
// reservationNumber/agreementNumber varchar(120)).
const RENTER_NAME_MAX = 160;
const RENTAL_NUMBER_MAX = 120;

export const rentalDetailInitialValues = (
  data?: AccountingExpense['rentalDetail']
) => ({
  notes: data?.notes || '',
  renterName: data?.renterName || '',
  rentalCompany: data?.rentalCompany || '',
  agreementNumber: data?.agreementNumber || '',
  reservationNumber: data?.reservationNumber || '',
  // Booking date and agreement number are no longer entered (Owner, 13 Oct
  // 2026); an older rental's saved values are kept and sent back on edit.
  bookingDate: formatCalendarDate(data?.bookingDate),
  // Dollars on the form, like every other money field here; integer cents
  // only at submit. A saved $0.00 estimate must come back as "0", while a
  // missing one stays blank.
  estimatedAmount:
    data?.estimatedAmountCents != null
      ? (data.estimatedAmountCents / 100).toString()
      : '',
});

const getIdsArray = <T extends IdObject>(array?: T[]) =>
  array?.map((item) => item.id) || [];

const getFormattedDate = (date?: string) => formatCalendarDate(date);

const getEvents = (events?: AccountingEvent[], initialEventId?: number) =>
  events?.map((event) => ({
    amount: event.amount,
    eventId: event.eventId,
    percent: event.percent,
  })) || [{ ...eventInitialValues, percent: 100, eventId: initialEventId }];

export const getInitialValues = (
  data?: AccountingExpense,
  initialEventId?: number,
  employeeId?: number
): FormValues => ({
  receiptUrl: data?.receiptUrl || '',
  description: data?.description || '',
  amount: data?.amount?.toString() || '',
  categories: getIdsArray(data?.categories),
  paidDate: getFormattedDate(data?.paidDate),
  receipt: getUploadedFile(data?.receiptUrl),
  events: getEvents(data?.events, initialEventId),
  paymentDate: getFormattedDate(data?.paymentDate),
  paymentMethodId: data?.paymentMethodId?.toString() || '',
  rentalDetail: rentalDetailInitialValues(data?.rentalDetail),
  employees: employeeId ? [employeeId] : getIdsArray(data?.employees),
  categoryAllocations:
    data?.categories?.map((category) => ({
      categoryId: category.id,
      amount:
        data.categories.length === 1
          ? String(data.amount)
          : String(category.ExpenseCategoryLink?.amount ?? ''),
    })) || [],
});

export const validationSchema = Yup.object().shape({
  paidDate: Yup.string(),
  description: Yup.string().min(5),
  paymentDate: Yup.string().required(),
  paymentMethodId: Yup.string().required(),
  employees: Yup.array().required().min(1),
  categories: Yup.array().required().min(1),
  receipt: Yup.object().required('File is a required field'),
  amount: Yup.number().required().typeError('Amount must be a number'),
  // Renter and rental company are NOT NULL, all-or-nothing on the server;
  // they and the reservation number are required here only when Car Rental
  // is selected.
  rentalDetail: Yup.object().when('categories', {
    is: (categories: number[]) => isCarRentalSelected(categories),
    then: (schema) =>
      schema.shape({
        // No longer entered (Owner, 13 Oct 2026); an older rental's saved
        // value is kept and sent back unchanged on edit.
        agreementNumber: Yup.string().trim().max(RENTAL_NUMBER_MAX),
        // The rental company's booking confirmation (Owner, 13 Oct 2026).
        reservationNumber: Yup.string()
          .trim()
          .max(RENTAL_NUMBER_MAX)
          .required('Reservation number is a required field'),
        rentalCompany: Yup.string().required(
          'Rental company is a required field'
        ),
        renterName: Yup.string()
          .trim()
          .max(RENTER_NAME_MAX)
          .required('Renter name is a required field'),
        // Optional: a blank estimate is "not known yet", not an error.
        estimatedAmount: Yup.number()
          .transform((value, original) =>
            original === '' || original == null ? undefined : value
          )
          .min(0)
          .typeError('Estimated amount must be a number'),
      }),
  }),
  categoryAllocations: Yup.array().test(
    'category-split',
    'Enter an amount for every category; category amounts must total the expense amount',
    function (rows) {
      const { amount, categories } = this.parent;
      if (categories?.length <= 1) {
        return true;
      }
      if (!rows || rows.length !== categories.length) {
        return false;
      }
      const ids = rows.map((row) => Number(row.categoryId));
      const total = Math.round(Number(amount) * 100);
      return (
        new Set(ids).size === categories.length &&
        categories.every((id: number) => ids.includes(Number(id))) &&
        rows.every(
          (row) =>
            /^-?\d+(\.\d{1,2})?$/.test(String(row.amount)) &&
            Math.sign(Number(row.amount)) === Math.sign(total)
        ) &&
        rows.reduce(
          (sum, row) => sum + Math.round(Number(row.amount) * 100),
          0
        ) === total
      );
    }
  ),
  events: Yup.array()
    .of(
      Yup.object().shape({
        eventId: Yup.number().required('Event is a required field'),
        amount: Yup.number().required('Amount is a required field'),
        percent: Yup.number()
          .required('Percent is a required field')
          .min(0)
          .test(
            'percent-amount',
            'Please fill out the amount first',
            function () {
              return this?.options?.context?.amount;
            }
          ),
      })
    )
    .test(
      'total-percentage',
      'The total percentage must be 100%',
      function (events) {
        const totalPercentage = events?.reduce(
          (sum, event) => sum + (+event.percent || 0),
          0
        );

        return Math.round((totalPercentage || 0) * 100) === 10000;
      }
    )
    .test(
      'total-amount',
      'Event amounts must total the expense amount',
      function (events) {
        const expenseAmount = Number(this.parent.amount);
        if (
          !Number.isFinite(expenseAmount) ||
          !events?.length ||
          events.some(
            (event) =>
              event.amount == null || !Number.isFinite(Number(event.amount))
          )
        ) {
          return true;
        }
        const allocationCents = events.reduce(
          (sum, event) => sum + Math.round(Number(event.amount) * 100),
          0
        );
        return allocationCents === Math.round(expenseAmount * 100);
      }
    ),
});

export const getFormattedDates = (values: FormValues) => ({
  paidDate: values.paidDate ? formatDate(values.paidDate, 'YYYY-MM-DD') : '',
  paymentDate: values.paymentDate
    ? formatDate(values.paymentDate, 'YYYY-MM-DD')
    : '',
});

// Builds the `rentalDetail` key of the create/edit request, matching the
// server's three states (main/src/services/accounting.js):
//   - Car Rental selected -> the object (dollars -> integer cents; a blank
//     estimate is sent as undefined, never 0).
//   - Car Rental removed from an expense that had saved detail (edit) ->
//     explicit null, the only signal that deletes it.
//   - otherwise -> the key is omitted, which leaves saved detail untouched.
// Callers must drop the form-shaped `values.rentalDetail` from their own
// spread before applying this.
export const getRentalDetailPayload = (
  values: FormValues,
  hadRentalDetail: boolean
) => {
  if (!isCarRentalSelected(values.categories)) {
    return hadRentalDetail ? { rentalDetail: null } : {};
  }
  const detail = values.rentalDetail;
  const estimate = String(detail.estimatedAmount ?? '').trim();
  return {
    rentalDetail: {
      notes: detail.notes || undefined,
      renterName: detail.renterName.trim(),
      agreementNumber: detail.agreementNumber.trim() || undefined,
      reservationNumber: detail.reservationNumber.trim() || undefined,
      rentalCompany:
        detail.rentalCompany as AccountingExpenseRentalDetail['rentalCompany'],
      estimatedAmountCents:
        estimate === '' ? undefined : Math.round(Number(estimate) * 100),
      bookingDate: detail.bookingDate
        ? formatDate(detail.bookingDate, 'YYYY-MM-DD')
        : undefined,
    },
  };
};

// The cent-exact split helpers live in utils/event-allocation.ts, shared with
// the stock transfer event split (T-L09); re-exported here unchanged.
export {
  calculateEventAmount,
  calculateEventAmounts,
  calculateEventPercents,
} from 'utils/event-allocation';
